import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityRecord,
  IGetRecordResponse,
  IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { PatientRelationship } from '@principle-theorem/principle-core';
import {
  IBasePatient,
  IPatient,
  IsPrimaryContact,
  PatientRelationshipType,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  FirestoreMigrate,
  getError,
  toNamedDocument,
  toTimestamp,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientRelationshipDestinationEntity,
  type IPatientRelationshipJobData as IPatientRelationshipBaseJobData,
} from '../../../destination/entities/patient-relationship';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  CorePracticePatient,
  ICorePracticePatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { PatientDestinationEntity } from './patients';

export const PATIENT_RELATIONSHIP_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: 'patientRelationships',
    label: 'Patient Relationships',
    description: '',
  },
});

export interface IPatientRelationshipDestinationRecord {
  patientRef: DocumentReference<IPatient>;
}

export type IPatientRelationshipJobData =
  IPatientRelationshipBaseJobData<CorePracticePatient>;

export class PatientRelationshipDestinationEntity extends BasePatientRelationshipDestinationEntity<
  CorePracticePatient,
  IPatientRelationshipDestinationRecord,
  IPatientRelationshipJobData
> {
  destinationEntity = PATIENT_RELATIONSHIP_DESTINATION_ENTITY;

  patientSourceEntity = new PatientSourceEntity();
  sourceCountComparison = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  buildMigrationData(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    data: IPatientRelationshipJobData
  ):
    | IPatientRelationshipJobData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord) {
    return data;
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    data: IPatientRelationshipJobData
  ): Promise<IDestinationEntityRecord> {
    const sourcePatientId = data.sourcePatient.data.id.toString();
    const patientRef = await translationMapHandler.getDestination<IPatient>(
      sourcePatientId,
      PATIENT_RESOURCE_TYPE
    );

    if (!patientRef) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        `Couldn't resolve patient`
      );
    }

    try {
      await this._addRelationshipsToPatient(
        translationMapHandler,
        data.sourcePatient.data,
        patientRef
      );
      return this._buildSuccessResponse(patientRef, data.sourcePatient.record);
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  private _buildSuccessResponse(
    patientRef: DocumentReference<IPatient>,
    patient: Pick<
      IGetRecordResponse<CorePracticePatient>['record'],
      'label' | 'uid' | 'id'
    >
  ): IDestinationEntityRecord<IPatientRelationshipDestinationRecord> {
    return {
      uid: patient.uid,
      label: patient.label,
      data: {
        patientRef,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: patient.id,
      migratedAt: toTimestamp(),
    };
  }

  private async _addRelationshipsToPatient(
    translationMap: TranslationMapHandler,
    sourcePatient: ICorePracticePatient,
    patientRef: DocumentReference<IPatient>
  ): Promise<void> {
    if (
      !sourcePatient.familyPatientId ||
      sourcePatient.familyPatientId === sourcePatient.id
    ) {
      return;
    }

    const familyHeadRef = await translationMap.getDestination<
      IBasePatient & IsPrimaryContact
    >(sourcePatient.familyPatientId.toString(), PATIENT_RESOURCE_TYPE);

    if (!familyHeadRef) {
      return;
    }

    const familyHead = await Firestore.getDoc(familyHeadRef);

    await FirestoreMigrate.patchDoc(patientRef, {
      primaryContact: {
        patient: toNamedDocument(familyHead),
        type: PatientRelationshipType.Unknown,
      },
    });

    await PatientRelationship.addPrimaryRelationship(
      await Firestore.getDoc(patientRef)
    );
  }
}
